import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell,
  Button,
  Card,
  Chip,
  Dialog,
  EmptyState,
  Icon,
  Input,
  SegmentedControl,
  Sheet,
  Skeleton
} from '../../../ui';
import { useAtualizacaoContexto } from '../../../contextos/AtualizacaoContexto';
import { usePerfilContexto } from '../../../contextos/PerfilContexto';
import { Conta as TipoConta } from '../../../servicos/contas/typeConta';
import { useContas } from '../../../servicos/contas/useContas';
import { Transacao } from '../../../servicos/transacoes/typeTransacao';
import { useTransacoes } from '../../../servicos/transacoes/useTransacoes';
import { primeiroDiaMesAtual, ultimoDiaMesAtual, valorParaTexto } from '../../../utils/Utils';
import { movimentoPorConta } from './movimento';
import TransferirSheet from './TransferirSheet';
import styles from './Conta.module.css';

// Tipos oficiais do serviço (typeConta.ts). Contas antigas com tipos legados
// ("Dinheiro", "Outros") caem no ícone/opção "Outro" sem quebrar nada.
const OPCOES_TIPO = [
  { id: 'Corrente', rotulo: 'Corrente', icone: 'account_balance' },
  { id: 'Poupança', rotulo: 'Poupança', icone: 'savings' },
  { id: 'Investimento', rotulo: 'Investimento', icone: 'trending_up' },
  { id: 'Outro', rotulo: 'Outro', icone: 'wallet' }
];

const iconeDoTipo = (tipo: string): string => OPCOES_TIPO.find((opcao) => opcao.id === tipo)?.icone ?? 'wallet';

/**
 * Tela de Contas — grid de cards com a movimentação do mês em cada conta
 * (receitas − despesas do mês atual), formulário em Sheet e exclusão com
 * confirmação em Dialog.
 */
export default function Conta() {
  const { contas, loading, createConta, updateConta, deleteConta } = useContas();
  const { perfil } = usePerfilContexto();
  const navigate = useNavigate();

  const paraISO = (data: Date): string => {
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${data.getFullYear()}-${mes}-${dia}`;
  };

  // Drill-down: card da conta abre as Movimentações já filtradas nela (mês atual).
  const verExtrato = (conta: TipoConta) =>
    navigate('/movimentacoes', {
      state: {
        contaIds: [conta.id?.toString() ?? ''],
        dataInicial: paraISO(primeiroDiaMesAtual()),
        dataFinal: paraISO(ultimoDiaMesAtual())
      }
    });
  // Primeira busca ainda no ar: nunca mostrar "nenhuma conta" antes de saber.
  const carregandoLista = contas.length === 0 && (loading || !!perfil.carregarContas);
  const { atualizar } = useAtualizacaoContexto();
  const { fetchTransacoes } = useTransacoes(false);

  const [transacoesMes, setTransacoesMes] = useState<Transacao[]>([]);

  const [formAberto, setFormAberto] = useState(false);
  const [transferindo, setTransferindo] = useState(false);
  const [editando, setEditando] = useState<TipoConta | null>(null);
  const [excluindo, setExcluindo] = useState<TipoConta | null>(null);
  const [menuAberto, setMenuAberto] = useState<number | null>(null);

  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<string>('Corrente');
  const [erroNome, setErroNome] = useState('');

  // Busca as transações do mês atual (1º ao último dia) para calcular a
  // movimentação de cada conta. Refaz quando alguma transação muda no app.
  useEffect(() => {
    let ativo = true;
    const buscar = async () => {
      const colecao = await fetchTransacoes({ dataInicial: primeiroDiaMesAtual(), dataFinal: ultimoDiaMesAtual() });
      if (ativo) setTransacoesMes(colecao || []);
    };
    buscar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atualizar]);

  // Fecha o menu de ações ao clicar em qualquer lugar fora dele.
  useEffect(() => {
    if (menuAberto === null) return;
    const fechar = () => setMenuAberto(null);
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [menuAberto]);

  // Receitas − despesas do mês por conta (só transações vinculadas à conta).
  const movimentacaoPorConta = useMemo(() => movimentoPorConta(transacoesMes), [transacoesMes]);

  const abrirCriacao = () => {
    setEditando(null);
    setNome('');
    setTipo('Corrente');
    setErroNome('');
    setFormAberto(true);
  };

  const abrirEdicao = (conta: TipoConta) => {
    setEditando(conta);
    setNome(conta.descricao);
    setTipo(OPCOES_TIPO.some((opcao) => opcao.id === conta.tipo) ? conta.tipo : 'Outro');
    setErroNome('');
    setFormAberto(true);
  };

  const fecharForm = () => {
    setFormAberto(false);
    setEditando(null);
  };

  const handleSalvar = async () => {
    if (!nome.trim()) {
      setErroNome('Dê um nome à conta para identificá-la.');
      return;
    }

    // Mesmo payload do serviço atual: descricao + tipo (saldo é calculado no backend).
    const payload = { descricao: nome.trim(), tipo: tipo as TipoConta['tipo'] };

    if (editando && editando.id !== undefined) {
      await updateConta(editando.id, payload);
    } else {
      await createConta(payload);
    }

    fecharForm();
  };

  const confirmarExclusao = async () => {
    if (excluindo && excluindo.id !== undefined) {
      await deleteConta([excluindo.id.toString()]);
    }
    setExcluindo(null);
  };

  const textoMovimentacao = (movimentacao: number) =>
    `${movimentacao > 0 ? '+' : ''}${valorParaTexto(movimentacao, true)} este mês`;

  return (
    <AppShell
      titulo="Contas"
      subtitulo="Suas contas bancárias, poupanças e carteiras: saldo atual e o que se moveu neste mês."
      acoes={
        contas.length > 0 ? (
          <>
            {contas.length >= 2 && (
              <Button variante="contorno" icone="swap_horiz" onClick={() => setTransferindo(true)}>
                Transferir
              </Button>
            )}
            <Button icone="add" onClick={abrirCriacao}>
              Nova conta
            </Button>
          </>
        ) : undefined
      }
    >
      {carregandoLista ? (
        <Skeleton tipo="cards" quantidade={3} altura={172} />
      ) : contas.length === 0 ? (
        <EmptyState
          icone="account_balance"
          titulo="Nenhuma conta cadastrada"
          descricao="Comece criando a conta por onde seu dinheiro circula: corrente, poupança, investimento ou carteira. Depois, cada movimentação registrada nela aparece aqui."
          acao={
            <Button icone="add" onClick={abrirCriacao}>
              Criar minha primeira conta
            </Button>
          }
        />
      ) : (
        <div className={styles.grid}>
          {contas.map((conta) => {
            const movimentacao = movimentacaoPorConta.get(conta.id ?? -1) || 0;
            return (
              <Card key={conta.id} className={styles.cardConta} interativo onClick={() => verExtrato(conta)}>
                <div className={styles.topo}>
                  <div className={styles.iconeTipo}>
                    <Icon nome={iconeDoTipo(conta.tipo)} tamanho={22} cor="var(--primary)" />
                  </div>
                  <div className={styles.menuArea}>
                    <button
                      type="button"
                      className={styles.menuBotao}
                      aria-label="Ações da conta"
                      onClick={(evento) => {
                        evento.stopPropagation();
                        setMenuAberto(menuAberto === conta.id ? null : conta.id ?? null);
                      }}
                    >
                      <Icon nome="more_vert" tamanho={18} />
                    </button>
                    {menuAberto === conta.id && (
                      <div className={styles.menu} onClick={(evento) => evento.stopPropagation()}>
                        <button type="button" className={styles.menuItem} onClick={() => abrirEdicao(conta)}>
                          <Icon nome="edit" tamanho={16} />
                          Editar
                        </button>
                        <button
                          type="button"
                          className={`${styles.menuItem} ${styles.menuItemPerigo}`}
                          onClick={() => setExcluindo(conta)}
                        >
                          <Icon nome="delete" tamanho={16} />
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.nome}>{conta.descricao}</div>
                <div className={styles.linhaTipo}>
                  <Chip rotulo={conta.tipo} icone={iconeDoTipo(conta.tipo)} />
                </div>
                <div className={`${styles.saldo} ${(conta.saldo ?? 0) < 0 ? styles.saldoNegativo : ''}`}>
                  {valorParaTexto(conta.saldo ?? 0, true)}
                </div>
                <div className={`${styles.movimentacao} ${movimentacao > 0 ? styles.positiva : ''}`}>
                  {textoMovimentacao(movimentacao)}
                </div>
              </Card>
            );
          })}
          <button type="button" className={styles.cardNova} onClick={abrirCriacao}>
            <Icon nome="add" tamanho={20} />
            Nova conta
          </button>
        </div>
      )}

      <TransferirSheet aberto={transferindo} onFechar={() => setTransferindo(false)} contas={contas} />

      <Sheet
        aberto={formAberto}
        onFechar={fecharForm}
        titulo={editando ? 'Editar conta' : 'Nova conta'}
        subtitulo={editando ? 'Ajuste o nome ou o tipo da conta.' : 'Cadastre uma conta para organizar suas movimentações.'}
        rodape={
          <>
            <Button variante="contorno" onClick={fecharForm}>
              Cancelar
            </Button>
            <Button icone="check" carregando={loading} onClick={handleSalvar}>
              {editando ? 'Salvar alterações' : 'Adicionar conta'}
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <Input
            label="Nome da conta"
            valor={nome}
            onChange={(valor) => {
              setNome(valor);
              if (erroNome) setErroNome('');
            }}
            placeholder="Ex.: Nubank, Itaú, Carteira..."
            icone="edit"
            erro={erroNome}
            obrigatorio
            autoFocus
            onEnter={handleSalvar}
          />
          <div className={styles.campo}>
            <span className={styles.rotuloCampo}>Tipo de conta</span>
            <SegmentedControl opcoes={OPCOES_TIPO} valor={tipo} onChange={setTipo} />
          </div>
        </div>
      </Sheet>

      <Dialog
        aberto={excluindo !== null}
        onFechar={() => setExcluindo(null)}
        titulo="Excluir conta"
        descricao={`Excluir "${excluindo?.descricao}"? As movimentações associadas serão desvinculadas, mas o histórico permanece.`}
        acoes={
          <>
            <Button variante="contorno" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            <Button variante="perigo" icone="delete" carregando={loading} onClick={confirmarExclusao}>
              Excluir
            </Button>
          </>
        }
      />
    </AppShell>
  );
}
